import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useMutation, useQuery } from '@app/QueryClient';
import {
	apiAddTeam,
	apiDeleteTeam,
	apiEditTeam,
	apiTeamApp,
} from '@app/Services/TeamMembers';
import {
	TApiTeamAppMember,
	TApiTeamAppMemberForm,
} from '@modules/TeamMembers/types';
import { apiError } from '@modules/utils/aprErrorHelper';

import { generatedKeys } from '../queryKeysFactory';

const { teamMembers } = generatedKeys;

export function useAddTeam(opt?: any) {
	const { t } = useTranslation('teams');
	const [parsedError, setParsedError] =
		useState<Record<keyof TApiTeamAppMemberForm, string>>();

	const mutation = useMutation(apiAddTeam, {
		...opt,
		moduleName: t('team'),
		operation: 'add',
	});

	const { mutate, error } = mutation;

	useEffect(() => {
		apiError<TApiTeamAppMemberForm>(mutation.error).then((e) =>
			setParsedError(e)
		);
	}, [error]);

	return {
		...mutation,
		doAddTeam: mutate,
		parsedError: parsedError,
	};
}

export function useEditTeam(opt?: any) {
	const { t } = useTranslation('teams');
	const [parsedError, setParsedError] =
		useState<Record<keyof TApiTeamAppMemberForm, string>>();

	const mutation = useMutation(apiEditTeam, {
		...opt,
		moduleName: t('team'),
		operation: 'update',
	});

	const { mutate, error } = mutation;

	useEffect(() => {
		apiError<TApiTeamAppMemberForm>(mutation.error).then((e) =>
			setParsedError(e)
		);
	}, [error]);

	return {
		...mutation,
		doEditTeam: mutate,
		parsedError: parsedError,
	};
}

export function useDeleteTeam(opt?: any) {
	const { t } = useTranslation('teams');

	const mutation = useMutation(apiDeleteTeam, {
		...opt,
		moduleName: t('team'),
		operation: 'delete',
	});

	const { mutateAsync } = mutation;

	return {
		...mutation,
		doDeleteTeam: mutateAsync,
	};
}

export function useTeamApp<
	TMappedType extends TApiTeamAppMember = TApiTeamAppMember
>(opt?: { formatTeam?: (d: TApiTeamAppMember) => TMappedType }) {
	const [teamList, setTeamList] = useState<TMappedType[]>([]);

	const q = useQuery({
		queryFn: apiTeamApp,
		staleTime: 1000 * 60 * 15, // 15 min
		queryKey: teamMembers.teamApp({}),
	});

	const formatBranch =
		opt?.formatTeam || ((d: TApiTeamAppMember) => d as TMappedType);

	const { data: teamData } = q as { data: { data: TApiTeamAppMember[] } };

	useEffect(() => {
		if (teamData) {
			const formattedData = teamData?.data?.map(formatBranch);
			setTeamList(formattedData);
		}
	}, [teamData, setTeamList]);

	return { ...q, data: teamList };
}
